import shortid from "shortid";

import {
  CybwellAdress,
  CybwellAdressDetail,
  CybwellBank,
  CybwellCompany,
  CybwellCompanyDetail,
  LegalForm,
} from "./types";
import {
  CybwellDoubleSignatureValues,
  CybwellSingleSignatureValues,
  SignatoryType,
  SignatureType,
} from "./types";

const mapSuggestedAddress = (response: CybwellAdress) => {
  return {
    legacyID: response.LEGACYID,
    label: response.LABEL,
  };
};

const mapResults = <T, P>(response: T | T[], mapper: (p: T) => P): P[] => {
  const mappedResults = Array.isArray(response)
    ? response.map(mapper)
    : [mapper(response)];

  return mappedResults.filter((res) => !!res);
};

const mapResult = <T, P>(response: T | T[], mapper: (p: T) => P): P =>
  Array.isArray(response) ? mapper(response[0]) : mapper(response);

const mapPerson: (
  person: CybwellCompanyDetail["PEOPLE"]["PERSON"][0],
) => SignatoryType = (person) => {
  if (
    [...CybwellSingleSignatureValues, ...CybwellDoubleSignatureValues].indexOf(
      person.ZeichnungsartID,
    ) === -1
  ) {
    return null;
  }
  return {
    id: shortid.generate(),
    firstName: person.Vorname,
    lastName: person.Name,
    signatureType:
      CybwellSingleSignatureValues.indexOf(person.ZeichnungsartID) !== -1
        ? SignatureType.Single
        : SignatureType.Double,
  };
};

const mapCompany = (company: CybwellCompany) => {
  return {
    ehraid: company.ehraid,
    name: company.name,
    address: {
      street: company.street,
      streetNr: company.hnr,
      zipCode: company.zip,
      city: company.town,
    },
  };
};

const mapLegalForm = (legalFormId: string): LegalForm => {
  switch (legalFormId) {
    case "1":
      return LegalForm.SOLE_PROPRIETORSHIP;
    case "2":
      return LegalForm.COLLECTIVE_PROPRIETORSHIP;
    case "3":
      return LegalForm.LIMITED_COMPANY;
    case "4":
      return LegalForm.LIMITED_LIABILITY_COMPANY;
    case "5":
      return LegalForm.COLLECTIVE;
    case "6":
      return LegalForm.ASSOCIATION;
    case "7":
      return LegalForm.FOUNDATION;
    case "8":
      return LegalForm.SPECIAL_LEGAL_FORM;
    case "9":
      return LegalForm.SWISS_BRANCH;
    case "10":
      return LegalForm.LIMITED_PARTNERSHIP;
    case "11":
      return LegalForm.ASSOCIATION_LIMITED_BY_SHARES;
    case "84":
      return LegalForm.INDIVISION;
    case "85":
      return LegalForm.PUBLIC_LAW_INSTITUTION;
    case "86":
      return LegalForm.NON_COMMERCIAL_POWER_OF_ATTORNEY;
    case "92":
      return LegalForm.FOREIGN_BRANCH;
    default:
      return null as LegalForm;
  }
};

const mapCompanyDetails = (company: CybwellCompanyDetail) => {
  const address = company.ADDRESSINFORMATION?.ADDRESS?.Receipt;
  const houseNumber = address && address.houseNumber ? address.houseNumber : "";
  const industryNogaCode = company.NOGA
    ? Array.isArray(company.NOGA?.INDUSTRY_CODE)
      ? (company.NOGA.INDUSTRY_CODE[0]?.INDUSTRY_CODE as string)
      : (company.NOGA.INDUSTRY_CODE?.INDUSTRY_CODE as string)
    : "";
  return {
    ehraid: company.ehraid,
    uid: company.uid,
    chid: company.chid,
    name: company.name,
    address: {
      street: address?.street,
      streetNr: houseNumber,
      zipCode: address?.swissZipCode,
      city: address?.town,
      country: { alpha2: address?.country },
    },
    phone: company.phone,
    purpose: company.purpose,
    capitalNom: company.capitalNom && Number(company.capitalNom),
    legalForm: mapLegalForm(company.legalformid),
    legalFormNameEN: company.legalFormNameEN,
    legalFormNameDE: company.legalFormNameDE,
    industryNogaCode: industryNogaCode.substring(0, 2),
    foundationDate: company.FirstDateOfTheActs,
    enteredInTradeRegistryDate: company.DateOfEntry,
    persons: company.PEOPLE?.PERSON
      ? mapResults(company.PEOPLE.PERSON, mapPerson)
      : [],
  };
};

const mapBankDetails = (bank: CybwellBank) => {
  return {
    code: bank.BCNr,
    name: bank.BankInstitut,
  };
};

export const mapGetBankByIBANResponse = (
  response: CybwellBank | CybwellBank[],
) => mapResult(response, mapBankDetails);

export const mapSuggestAddressResponse = (
  response: CybwellAdress | CybwellAdress[],
) => mapResults(response, mapSuggestedAddress);

export const mapAddressDetailResponse = (response: CybwellAdressDetail) => {
  return {
    street: response.STR,
    streetNr: response.HNR,
    city: response.CITY,
    zipCode: response.ZIP,
  };
};

export const mapSuggestCompanyResponse = (
  response: CybwellCompany | CybwellCompany[],
) => mapResults(response, mapCompany);

export const mapCompanyDetailResponse = (
  response: CybwellCompanyDetail | CybwellCompanyDetail[],
) => mapResult(response, mapCompanyDetails);
